import { useMemo } from 'react';
import { FrameIdIndex } from 'store/dealManagement/types';
import { FromTo } from 'components/common/types/Range.types';
import { UsePlannerSegmentsIndexRange, ValueAndColor } from './usePlannerActions.types';
import { PLANNER_INDEX_SEGMENT_COLOR_RGB } from '../PlannerMap/utils/mapColors';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const usePlannerSegmentsIndexRange = (frameIdIndexes: FrameIdIndex[]): UsePlannerSegmentsIndexRange | null => {
  const frameIdIndexesSortedAscending = useMemo(() => {
    return frameIdIndexes.map((frameIdIndex) => frameIdIndex.index).sort((a, b) => a - b);
  }, [frameIdIndexes]);

  const segmentsIndexesRange = useMemo(() => {
    const indexesBiggerThanZero = frameIdIndexesSortedAscending.filter((index) => index !== 0);

    const min = indexesBiggerThanZero.length ? Math.min(...indexesBiggerThanZero) : 0;
    const max = indexesBiggerThanZero.length ? Math.max(...indexesBiggerThanZero) : 0;

    return {
      min,
      max,
      getProportions: (ratio: number) => min + ratio * (max - min),
    };
  }, [frameIdIndexesSortedAscending]);

  const getNumberOfElementsInRange = <T extends number | FromTo>(frameIndex: ValueAndColor<T>): number =>
    frameIdIndexesSortedAscending.filter((segmentIndexValue) => frameIndex.isIndexValueInRange(segmentIndexValue))
      .length;

  const getIsMiddleIndexValueInRange = (indexValue: number, segmentValue: FromTo): boolean =>
    indexValue !== 0 &&
    indexValue > segmentsIndexesRange.min &&
    indexValue >= segmentValue.from &&
    indexValue <= segmentValue.to;

  const segmentsWithValuesAndColors = useMemo(
    () => ({
      zero: {
        colorRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.IS_ZERO_RGB,
        textRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.TEXT_LIGHT,
        value: 0,
        isIndexValueInRange(indexValue: number) {
          return indexValue === this.value;
        },
      },
      lessThan: {
        colorRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.LESS_THAN_RGB,
        textRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.TEXT_LIGHT,
        value: segmentsIndexesRange.getProportions(0.25),
        isIndexValueInRange(indexValue: number) {
          return indexValue !== 0 && indexValue <= this.value;
        },
      },
      firstRange: {
        colorRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.FIRST_RANGE_RGB,
        textRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.TEXT_LIGHT,
        value: {
          from: segmentsIndexesRange.getProportions(0.25),
          to: segmentsIndexesRange.getProportions(0.5),
        },
        isIndexValueInRange(indexValue: number) {
          return getIsMiddleIndexValueInRange(indexValue, this.value);
        },
      },
      secondRange: {
        colorRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.SECOND_RANGE_RGB,
        textRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.TEXT_LIGHT,
        value: {
          from: segmentsIndexesRange.getProportions(0.5),
          to: segmentsIndexesRange.getProportions(0.75),
        },
        isIndexValueInRange(indexValue: number) {
          return getIsMiddleIndexValueInRange(indexValue, this.value);
        },
      },
      higherThan: {
        colorRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.HIGHER_THAN_RGB,
        textRGB: PLANNER_INDEX_SEGMENT_COLOR_RGB.TEXT_LIGHT,
        value: segmentsIndexesRange.getProportions(0.75),
        isIndexValueInRange(indexValue: number) {
          return indexValue !== 0 && indexValue >= this.value;
        },
      },
    }),
    [segmentsIndexesRange],
  );

  const getSegmentRGBColorByIndexValue = (indexValue: number): number[] | null => {
    if (!frameIdIndexes.length || indexValue === -1) return null;

    return (
      Object.values(segmentsWithValuesAndColors).find((entry) => {
        return entry?.isIndexValueInRange(indexValue);
      })?.colorRGB ?? null
    );
  };

  const getTextRGBColorByIndexValue = (indexValue: number): number[] | null => {
    if (!frameIdIndexes.length || indexValue === -1) return null;

    return (
      Object.values(segmentsWithValuesAndColors).find((entry) => {
        return entry?.isIndexValueInRange(indexValue);
      })?.textRGB ?? null
    );
  };

  return !frameIdIndexes.length
    ? null
    : {
        ...segmentsWithValuesAndColors,
        min: segmentsIndexesRange.min,
        max: segmentsIndexesRange.max,
        getSegmentRGBColorByIndexValue,
        getTextRGBColorByIndexValue,
        getNumberOfElementsInRange,
      };
};
